/**
 * Live trace metrics — top-N tenants / event types / reservations / lockers.
 *
 * Sibling component to Sstv2TracesTab.tsx; rendered between the filter Paper
 * and the trace table. Click a row to apply that value as a filter on the
 * parent's query state. Click the top-level "Show all" toggle to expand the
 * section: progress bars become visible, event types fetch all values, and
 * tenant/reservation/locker columns reveal a "Show 10 more" pagination link.
 */
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import { ExpandLess, ExpandMore } from "@mui/icons-material"
import { FC, useMemo, useState } from "react"

import { fmtRelative } from "./formatters"
import { useGetTraceMetricsQuery } from "./sstv2TraceApi"
import type { TraceMetricDimension, TraceMetricItem } from "./types"

const POLL_INTERVAL_MS = 10_000
const TOP_N = 10
const PAGE_SIZE = 10
const EVENT_TYPE_EXPAND_LIMIT = 200

export type TraceMetricFilterTarget =
  | { kind: "tenant"; value: string }
  | { kind: "eventType"; value: string }
  | { kind: "reservation"; value: string }
  | { kind: "locker"; value: string }

export type Sstv2TraceMetricsSectionProps = {
  onSelectFilter: (target: TraceMetricFilterTarget) => void
  tenantNameById: Map<string, string>
}

type ColumnDef = {
  dimension: TraceMetricDimension
  title: string
  filterKind: TraceMetricFilterTarget["kind"]
  monospace?: boolean
}

const COLUMNS: ColumnDef[] = [
  { dimension: "tenant", title: "Top Tenants", filterKind: "tenant" },
  {
    dimension: "eventType",
    title: "Event Types",
    filterKind: "eventType",
    monospace: true,
  },
  {
    dimension: "reservation",
    title: "Top Reservations",
    filterKind: "reservation",
    monospace: true,
  },
  {
    dimension: "locker",
    title: "Top Lockers",
    filterKind: "locker",
    monospace: true,
  },
]

const MetricRow: FC<{
  item: TraceMetricItem
  label: string
  monospace?: boolean
  showBar: boolean
  barValuePct: number
  onClick: () => void
}> = ({ item, label, monospace, showBar, barValuePct, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 0.75,
      py: 0.4,
      borderRadius: 1,
      cursor: "pointer",
      "&:hover": { bgcolor: "action.hover" },
    }}
  >
    <Stack direction="row" alignItems="center" gap={1}>
      <Tooltip title={item.value} disableInteractive>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: monospace ? "monospace" : undefined,
            fontSize: 12,
          }}
        >
          {label}
        </Typography>
      </Tooltip>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {fmtRelative(item.lastIncrementedAt)}
      </Typography>
      <Chip
        size="small"
        label={item.count}
        sx={{
          height: 20,
          minWidth: 32,
          fontWeight: 700,
          "& .MuiChip-label": { px: 0.75 },
        }}
      />
    </Stack>
    {showBar && (
      <LinearProgress
        variant="determinate"
        value={Math.max(2, Math.min(100, barValuePct))}
        sx={{
          height: 3,
          borderRadius: 2,
          mt: 0.5,
          bgcolor: "action.hover",
        }}
      />
    )}
  </Box>
)

const MetricColumn: FC<{
  column: ColumnDef
  expanded: boolean
  resolveLabel: (value: string) => string
  onSelectFilter: Sstv2TraceMetricsSectionProps["onSelectFilter"]
}> = ({ column, expanded, resolveLabel, onSelectFilter }) => {
  const [extraPages, setExtraPages] = useState(0)
  const isPaginated = column.dimension !== "eventType"

  const limit = expanded
    ? isPaginated
      ? TOP_N + extraPages * PAGE_SIZE
      : EVENT_TYPE_EXPAND_LIMIT
    : TOP_N

  const { data, isFetching } = useGetTraceMetricsQuery(
    { dimension: column.dimension, limit },
    { pollingInterval: POLL_INTERVAL_MS },
  )

  const items: TraceMetricItem[] = useMemo(
    () => data?.data?.items ?? [],
    [data],
  )
  const truncated = !!data?.data?.truncated
  const maxCount = items[0]?.count ?? 1

  const canShowMore =
    expanded && isPaginated && items.length >= limit && !truncated

  return (
    <Box sx={{ flex: 1, minWidth: 220 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 0.75 }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          {column.title}
        </Typography>
        {truncated && (
          <Tooltip title="Result set was capped — only the first 1000 items were considered for top-N.">
            <Chip
              size="small"
              label="truncated"
              variant="outlined"
              color="warning"
              sx={{ height: 18, "& .MuiChip-label": { px: 0.75, fontSize: 10 } }}
            />
          </Tooltip>
        )}
      </Stack>
      {items.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {isFetching ? "Loading..." : "No data yet."}
        </Typography>
      ) : (
        <>
          <Stack spacing={0.25}>
            {items.map((item) => (
              <MetricRow
                key={item.value}
                item={item}
                label={resolveLabel(item.value)}
                monospace={column.monospace}
                showBar={expanded}
                barValuePct={(item.count / maxCount) * 100}
                onClick={() =>
                  onSelectFilter({
                    kind: column.filterKind,
                    value: item.value,
                  })
                }
              />
            ))}
          </Stack>
          {canShowMore && (
            <Box sx={{ mt: 0.5, pl: 0.75 }}>
              <MuiLink
                component="button"
                onClick={() => setExtraPages((p) => p + 1)}
                sx={{
                  fontSize: 11,
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                Show {PAGE_SIZE} more
              </MuiLink>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export const Sstv2TraceMetricsSection: FC<Sstv2TraceMetricsSectionProps> = ({
  onSelectFilter,
  tenantNameById,
}) => {
  const [expanded, setExpanded] = useState(false)

  const resolvers: Record<TraceMetricDimension, (value: string) => string> =
    useMemo(
      () => ({
        tenant: (value) => tenantNameById.get(value) ?? value,
        eventType: (value) => value,
        reservation: (value) => value,
        locker: (value) => value,
      }),
      [tenantNameById],
    )

  return (
    <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            letterSpacing: 1.2,
            color: "text.primary",
          }}
        >
          Metrics
        </Typography>
        <Typography variant="caption" color="text.secondary">
          lifetime totals — click a row to filter the trace list
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          onClick={() => setExpanded((e) => !e)}
          startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          sx={{ textTransform: "none" }}
        >
          {expanded ? "Show less" : "Show all"}
        </Button>
      </Stack>
      <Stack
        direction="row"
        spacing={3}
        useFlexGap
        flexWrap="wrap"
        alignItems="flex-start"
      >
        {COLUMNS.map((column) => (
          <MetricColumn
            key={column.dimension}
            column={column}
            expanded={expanded}
            resolveLabel={resolvers[column.dimension]}
            onSelectFilter={onSelectFilter}
          />
        ))}
      </Stack>
    </Paper>
  )
}

export default Sstv2TraceMetricsSection
